/** Office: who is working, on what, right now. The app draws a pixel-art office; here each agent has a desk. */

import { CircleExclamation, Display, Persons, Pulse } from '@gravity-ui/icons';

import { ArtEmpty, ListGroup, ListRow, Stat, StatusMark } from '../../components/ui.tsx';
import type { Workbench } from '../useWorkbench.ts';
import { Avatar } from './Avatar.tsx';
import { RemoteView } from './RemoteView.tsx';
import { agentState } from './shared.tsx';

export function OfficeView({ w }: { w: Workbench }) {
  const a = w.activity.data;
  const agents = a?.agents ?? [];
  const waiting = (a?.pendingApprovals ?? 0) + (a?.openDecisions ?? 0);
  return (
    <RemoteView>
      <div className="art-stats">
        <Stat tone="teal" icon={<Persons />} label="At their desks" value={a ? agents.length : undefined} detail={a ? `${agents.filter((x) => x.teamId !== 'solo').length} on teams, ${agents.filter((x) => x.teamId === 'solo').length} solo` : ''} />
        <Stat tone="blue" icon={<Pulse />} label="Working" value={a?.working} detail="thinking or using a tool" />
        <Stat tone="amber" icon={<CircleExclamation />} label="Waiting on you" value={a ? waiting : undefined} detail={waiting ? 'approvals and questions' : 'nothing waiting'} needsYou={waiting > 0} />
      </div>
      {agents.length ? (
        <ListGroup title="Desks" count={agents.length} description="Each agent at work on this machine, and what it is doing now.">
          {agents.map((x) => {
            const s = agentState(x.status);
            const doing = x.currentTool ? `using ${x.currentTool}` : x.tasks[0] ? `${x.tasks[0].id} ${x.tasks[0].title}` : x.title;
            return (
              <ListRow
                key={x.sessionId}
                icon={<Avatar name={x.name} size="sm" />}
                title={x.name}
                description={[x.isOpsManager ? 'Operations manager' : x.teamId === 'solo' ? 'Solo' : x.teamId, doing].filter(Boolean).join(' · ')}
                status={<StatusMark state={s.state} label={`${s.label[0]!.toUpperCase()}${s.label.slice(1)}`} />}
              />
            );
          })}
        </ListGroup>
      ) : (
        <ArtEmpty tone="teal" icon={<Display />} title="The office is empty">
          Start a team or open a chat in the app and its agents take their desks.
        </ArtEmpty>
      )}
    </RemoteView>
  );
}
